import PreAccountingApi from "./PreAccountingApi";
import {
  PreAccAccNo,
  PreAccDimCode,
  PreAccEditDimension,
  PreAccEditDimensionDefaultProps,
  PreAccEditItem,
  PreAccEditItemDefaultProps,
  PreAccEditorData,
  PreAccEditorFlags,
  PreAccEditorHead,
  PreAccEditorHeadDefaultProps,
  PreAccItemTemplate,
  PreAccMixCreateRequest,
  PreAccMixCreateResult,
  PreAccMixType,
  PreAccReAccountResult,
  PreAccSaveRequest,
  PreAccSaveResult,
  PreAccStoreDoc,
} from "../models/PreAccEditing";
import { t } from "@/plugins/i18n";

// A kontír-szerkesztő singleton store-ja — szerkezete a tranzakció modul
// TransactionStore-jának másolata (delta-tömbök + original snapshotok + oldalankénti
// kijelölések), a tartalma a K2D w_popup_fokonyv adatköre.
export class PreAccountingStore {
  //----------- Szerkesztett számlá(k) -----------//
  // Több számla kijelölése esetén (K2D lapozó) itt a teljes lista; egy számlánál 1 elem.
  invoiceRefs: { code: string; invNo: number }[] = [];
  currentIndex = 0;

  head: PreAccEditorHead = { ...PreAccEditorHeadDefaultProps };
  originalHead: PreAccEditorHead = { ...PreAccEditorHeadDefaultProps };
  // Betöltéskori kontír-státusz — a mentés optimista egyeztetéséhez (K2D snapshot-minta)
  originalAccStatus = 1;
  editable = false;
  reAccountable = false;

  //----------- Kontírtételek -----------//
  items: PreAccEditItem[] = [];
  originalItems: PreAccEditItem[] = [];

  isItemsLoading = false;

  newItems: PreAccEditItem[] = [];
  modifiedItems: PreAccEditItem[] = [];
  deletedItems: number[] = [];

  selectedItem: PreAccEditItem = { ...PreAccEditItemDefaultProps };
  positiveSelectedItem: PreAccEditItem = { ...PreAccEditItemDefaultProps };
  negativeSelectedItem: PreAccEditItem = { ...PreAccEditItemDefaultProps };

  //----------- Dimenzió-bontás (4-12.) -----------//
  dimensionsRows: PreAccEditDimension[] = [];
  originalDimensionsRows: PreAccEditDimension[] = [];

  selectedDimension: PreAccEditDimension = { ...PreAccEditDimensionDefaultProps };
  positiveSelectedDimension: PreAccEditDimension = { ...PreAccEditDimensionDefaultProps };
  negativeSelectedDimension: PreAccEditDimension = { ...PreAccEditDimensionDefaultProps };

  newDimensions: PreAccEditDimension[] = [];
  modifiedDimensions: PreAccEditDimension[] = [];
  deletedDimensions: number[] = [];

  positiveSelectedDimCodeType = 5;
  negativeSelectedDimCodeType = 5;

  isReadOnlyView = false;

  //----------- Lookupok -----------//
  accNos: PreAccAccNo[] = [];
  dimCodes: PreAccDimCode[] = [];
  bookTypes: { id: number; name: string }[] = [];
  detailedTypes: { id: number; name: string }[] = [];
  defaultDetailedType = 6;
  accGroups: { id: number; name: string }[] = [];
  // KÉK bizonylat periódus-választója (mmbokperiod, 48 legutóbbi)
  periods: { id: number; nicName: string; fromDate: string; toDate: string }[] = [];
  mixTypes: PreAccMixType[] = [];
  flags: PreAccEditorFlags = { extBook: false, dim49: false, checkDetType: false, book: false, projectMenu: false, dimTypeLive: {} };

  // Új MIX bizonylat felvitele (K2D w_response_mix pb_insert, ii_oper=2):
  // a MIX-fejléc mezői ilyenkor írhatók
  isCreateMode = false;

  // Az aktív mozgásfajta betárolás/kitárolás választó-listája (K2D w_popup_storein_out)
  storeDocs: PreAccStoreDoc[] = [];
  isStoreDocsLoading = false;

  // Sablonos módosítás: a K2D oldalanként megjegyzi a sablont (inv_tart_template /
  // inv_kov_template), így újra megnyitva az előző értékek és pipák jönnek vissza.
  // Kulcs a grid oldala: 'negative' (Tartozik) | 'positive' (Követel) | 'both'.
  itemTemplates: Record<string, PreAccItemTemplate | null> = {
    negative: null,
    positive: null,
    both: null,
  };

  serCars: any[] = [];
  projects: any[] = [];
  pos: any[] = [];
  users: any[] = [];
  stations: any[] = [];
  tools: any[] = [];

  // K2D il_accitem_id: az új tételek/bontás-sorok negatív ideiglenes azonosítója,
  // ez köti össze az új tételt a bontás-soraival mentés előtt
  private tempId = 0;

  nextTempId(): number {
    this.tempId--;
    return this.tempId;
  }

  get currentRef(): { code: string; invNo: number } | null {
    return this.invoiceRefs.length > 0 ? this.invoiceRefs[this.currentIndex] : null;
  }

  get selectedMixType(): PreAccMixType | null {
    return this.mixTypes.find((t) => t.id === this.head.invType) ?? null;
  }

  // A K2D w_response_mix buttonclicked kapui: a kiválasztott MIX-típus flagjei döntik el,
  // hogy a három tároló-mező közül melyik választható — egyszerre pontosan egy (vagy egyik sem)
  // K2D: a típus stock_value_diff flagje dönti el, hogy KÉK bizonylatról van-e szó —
  // create módban a típusválasztás után azonnal, meglévő bizonylaton a fejből
  get isStockValueDiff(): boolean {
    if (this.isCreateMode) {
      return Number(this.selectedMixType?.stockValueDiff ?? 1) > 1;
    }
    return Number(this.head.stockValueDiff) > 1;
  }

  get activeStoreKind(): "storein" | "gstorein" | "storeout" | null {
    const t = this.selectedMixType;
    if (!t || t.id <= 1 || !t.isStock) return null;
    if (t.isOut) return "storeout";
    return t.isFactory ? "storein" : "gstorein";
  }

  // Az aktív mozgásfajta választó-listájának (újra)töltése; a betárolás-listák a
  // kiválasztott partnerre is szűrnek (K2D :supplier)
  async loadStoreDocs() {
    const kind = this.activeStoreKind;
    if (!this.isCreateMode || !kind) {
      this.storeDocs = [];
      return;
    }
    this.isStoreDocsLoading = true;
    try {
      this.storeDocs = await PreAccountingApi.getStoreDocs(
        kind,
        this.head.invType,
        kind === "storeout" ? 1 : this.head.partner || 1
      );
    } catch {
      this.storeDocs = [];
    } finally {
      this.isStoreDocsLoading = false;
    }
  }

  // Bármely mentetlen módosítás — a K2D-hű tiltásokhoz (lapozó/auto-kontír/bezárás)
  get hasChanges(): boolean {
    return (
      this.newItems.length > 0 ||
      this.modifiedItems.length > 0 ||
      this.deletedItems.length > 0 ||
      this.newDimensions.length > 0 ||
      this.modifiedDimensions.length > 0 ||
      this.deletedDimensions.length > 0 ||
      this.head.isVatScaling !== this.originalHead.isVatScaling ||
      this.head.accGroup !== this.originalHead.accGroup
    );
  }

  // Sikeres mentés után: a tétel-delták törlése és a fej-snapshot frissítése, hogy a
  // hasChanges (kilépés-őr, lapozó, auto-kontír) tiszta állapotot lásson
  markSaved() {
    this.resetDeltas();
    this.originalHead = JSON.parse(JSON.stringify(this.head));
  }

  // A Mégse: a tétel-delták és a fej-módosítások (ÁFA-arányosítás, kontír-csoport) is
  // elvesznek, hogy a kilépés-őr (beforeRouteLeave) ne kérdezzen rá
  discardChanges() {
    this.resetDeltas();
    this.head.isVatScaling = this.originalHead.isVatScaling;
    this.head.accGroup = this.originalHead.accGroup;
  }

  resetDeltas() {
    this.newItems = [];
    this.modifiedItems = [];
    this.deletedItems = [];
    this.newDimensions = [];
    this.modifiedDimensions = [];
    this.deletedDimensions = [];
    this.tempId = 0;
    // Új számla = új sablon és üres kijelölés (K2D: a template objektum az ablakhoz él)
    this.itemTemplates = { negative: null, positive: null, both: null };
  }

  // Egy tétel „módosított"-státuszának karbantartása: csak akkor marad a
  // modifiedItems-ben, ha tényleg eltér a betöltött állapottól. Az inline
  // szerkesztés és a sablonos módosítás is ezt használja.
  syncModified(item: PreAccEditItem) {
    if (item.id <= 0) return; // új tétel — a newItems tartja számon
    const original = this.originalItems.find((x) => x.id === item.id);
    if (!original) return;

    const fields: (keyof PreAccEditItem)[] = [
      "accCode", "dimType", "detailedType", "total", "currency",
      "dimCode1", "dimCode2", "dimCode3", "note", "accDate",
    ];
    const changed = fields.some((key) =>
      key === "accDate"
        ? String(item[key]).split("T")[0] !== String(original[key]).split("T")[0]
        : item[key] != original[key]
    );

    if (changed) {
      if (!this.modifiedItems.some((x) => x.id === item.id)) {
        this.modifiedItems.push(item);
      }
    } else {
      this.modifiedItems = this.modifiedItems.filter((x) => x.id !== item.id);
    }
  }

  //----------- Betöltés -----------//

  // Kliens-oldali üres felvitel-állapot — akkor is megnyílik az űrlap, ha a
  // kezdőcsomag (lookupok) nem érhető el
  initCreateDefaults() {
    const today = new Date().toISOString().split("T")[0];
    this.head = {
      ...PreAccEditorHeadDefaultProps,
      code: "MIX",
      prefix: "MIX",
      isMix: true,
      accStatus: 2,
      accDate: today,
      insDate: today,
      hasAccGroup: true,
      currency: "HUF",
    };
    this.originalHead = JSON.parse(JSON.stringify(this.head));
    this.originalAccStatus = 2;
    this.editable = true;
    this.reAccountable = false;
    this.items = [];
    this.originalItems = [];
    this.dimensionsRows = [];
    this.originalDimensionsRows = [];
    this.resetDeltas();
    this.selectedItem = { ...PreAccEditItemDefaultProps };
    this.positiveSelectedItem = { ...PreAccEditItemDefaultProps };
    this.negativeSelectedItem = { ...PreAccEditItemDefaultProps };
    this.selectedDimension = { ...PreAccEditDimensionDefaultProps };
    this.isCreateMode = true;
  }

  // Új MIX felvitel kezdőállapota: üres, írható fej + lookupok, tételek nélkül
  async loadCreateData() {
    this.isItemsLoading = true;
    try {
      const data: PreAccEditorData = await PreAccountingApi.getCreateData();
      this.head = data.head;
      this.originalHead = JSON.parse(JSON.stringify(data.head));
      this.originalAccStatus = data.originalAccStatus;
      this.editable = data.editable;
      this.reAccountable = data.reAccountable;
      this.items = [];
      this.originalItems = [];
      this.dimensionsRows = [];
      this.originalDimensionsRows = [];

      this.accNos = data.accNos;
      this.dimCodes = data.dimCodes;
      this.bookTypes = data.bookTypes;
      this.detailedTypes = data.detailedTypes;
      this.defaultDetailedType = data.defaultDetailedType;
      this.accGroups = data.accGroups;
      this.periods = data.periods ?? [];
      this.mixTypes = data.mixTypes ?? [];
      this.flags = data.flags;

      this.resetDeltas();
      this.selectedItem = { ...PreAccEditItemDefaultProps };
      this.positiveSelectedItem = { ...PreAccEditItemDefaultProps };
      this.negativeSelectedItem = { ...PreAccEditItemDefaultProps };
      this.selectedDimension = { ...PreAccEditDimensionDefaultProps };
      this.isCreateMode = true;
      // A default Típushoz tartozó betárolás/kitárolás lista (nem várjuk meg)
      this.loadStoreDocs();
    } finally {
      this.isItemsLoading = false;
    }
  }

  async loadEditorData(code: string, invNo: number) {
    this.isItemsLoading = true;
    this.isCreateMode = false;
    this.storeDocs = [];
    try {
      const data: PreAccEditorData = await PreAccountingApi.getEditorData(code, invNo);
      this.head = data.head;
      this.originalHead = JSON.parse(JSON.stringify(data.head));
      this.originalAccStatus = data.originalAccStatus;
      this.editable = data.editable;
      this.reAccountable = data.reAccountable;

      // A DTO-kon nincsenek UI-flagek (sel/inEdit/newDimensions) — a defaultokkal
      // egészítjük ki, különben a lenyitó/kijelölés nem lenne reaktív
      this.items = (data.items ?? []).map((x) => ({
        ...PreAccEditItemDefaultProps,
        ...x,
        newDimensions: [],
      }));
      this.originalItems = JSON.parse(JSON.stringify(this.items));
      this.dimensionsRows = (data.dimensions ?? []).map((x) => ({
        ...PreAccEditDimensionDefaultProps,
        ...x,
      }));
      this.originalDimensionsRows = JSON.parse(JSON.stringify(this.dimensionsRows));

      this.accNos = data.accNos;
      this.dimCodes = data.dimCodes;
      this.bookTypes = data.bookTypes;
      this.detailedTypes = data.detailedTypes;
      this.defaultDetailedType = data.defaultDetailedType;
      this.accGroups = data.accGroups;
      this.periods = data.periods ?? [];
      this.mixTypes = data.mixTypes ?? [];
      this.flags = data.flags;

      this.resetDeltas();
      this.selectedItem = { ...PreAccEditItemDefaultProps };
      this.positiveSelectedItem = { ...PreAccEditItemDefaultProps };
      this.negativeSelectedItem = { ...PreAccEditItemDefaultProps };
      this.selectedDimension = { ...PreAccEditDimensionDefaultProps };
    } finally {
      this.isItemsLoading = false;
    }
  }

  // A kísérő-lookupok (VII-X. dim fülek) — elég egyszer betölteni a szerkesztő életére
  async loadDimLookups() {
    const [cars, projects, pos, users, stations, tools] = await Promise.all([
      PreAccountingApi.getEditorCars(),
      PreAccountingApi.getEditorProjects(),
      PreAccountingApi.getEditorPos(),
      PreAccountingApi.getEditorUsers(),
      PreAccountingApi.getEditorStations(),
      PreAccountingApi.getEditorTools(),
    ]);
    this.serCars = cars.map((x) => ({ ...x, idString: String(x.id) }));
    this.projects = projects.map((x) => ({ ...x, idString: String(x.id) }));
    this.pos = pos.map((x) => ({ ...x, idString: String(x.id) }));
    this.users = users;
    this.stations = stations;
    this.tools = tools;
  }

  //----------- Mentés / auto-kontír (K2D pb_ok, pb_cont) -----------//

  isSaving = false;

  private static dayOnly(value: string): string {
    return String(value ?? "").split("T")[0];
  }

  // A K2D a teljes kontírozást írja vissza: minden sor + a törölt azonosítók
  // Új MIX bizonylat mentésének törzse (K2D pb_ok): a fej-mezők + a teljes kontír-állapot
  buildMixCreatePayload(): PreAccMixCreateRequest {
    const base = this.buildSavePayload();
    return {
      invType: this.head.invType,
      partner: this.head.partner || 1,
      storeIn: Number(this.head.storeIn) || 1,
      gStoreIn: Number(this.head.gStoreIn) || 1,
      storeOut: Number(this.head.storeOut) || 1,
      accGroup: this.head.accGroup,
      accEvent: this.head.accEvent,
      note: this.head.note,
      currency: this.head.currency,
      total: Number(this.head.total) || 0,
      currTotal: Number(this.head.currTotal) || 0,
      exchangeRate: Number(this.head.exchangeRate) || 1,
      accDate: PreAccountingStore.dayOnly(this.head.accDate),
      parts: Number(this.head.parts) || 1,
      period: Number(this.head.period) || 1,
      items: base.items,
      dimensions: base.dimensions,
    };
  }

  async createMix(): Promise<PreAccMixCreateResult> {
    this.isSaving = true;
    try {
      return await PreAccountingApi.createMix(this.buildMixCreatePayload());
    } finally {
      this.isSaving = false;
    }
  }

  buildSavePayload(): PreAccSaveRequest {
    // K2D w_response_mix: KÉK (stock_value_diff > 1) bizonylaton a szerkesztő 3-as
    // módba vált és a FEJ is írható (dátum, esemény, megjegyzés, típus). A pb_ok a
    // mentés előtt a fej könyvelési dátumát minden T/K tételre rámásolja (:952-1011).
    const kekHead = this.head.isMix && this.isStockValueDiff && !this.isReadOnlyView && !this.isCreateMode;
    const headAccDate = PreAccountingStore.dayOnly(this.head.accDate);
    return {
      originalAccStatus: this.originalAccStatus,
      isVatScaling: this.head.isVatScaling,
      accGroup: this.head.accGroup,
      // KÉK: a cikk és a periódus a fejen él (a backend csak MIX-en veszi figyelembe)
      parts: this.head.parts,
      period: this.head.period,
      ...(kekHead
        ? {
            accDate: headAccDate,
            accEvent: this.head.accEvent,
            note: this.head.note,
            invType: this.head.invType,
          }
        : {}),
      items: this.items.map((x) => ({
        id: x.id,
        accCode: x.accCode,
        accSign: x.accSign,
        dimType: x.dimType,
        detailedType: x.detailedType,
        total: Number(x.total) || 0,
        currency: x.currency,
        accDate: kekHead && headAccDate ? headAccDate : PreAccountingStore.dayOnly(x.accDate),
        dimCode1: x.dimCode1,
        dimCode2: x.dimCode2,
        dimCode3: x.dimCode3,
        note: x.note,
      })),
      dimensions: this.dimensionsRows.map((d) => ({
        id: d.id,
        head: d.head,
        dimCodeType: d.dimCodeType,
        dimCode: d.dimCode,
        total: Number(d.total) || 0,
        project: d.project,
        poId: d.poId,
        userId: d.userId,
        stationId: d.stationId,
        toolId: d.toolId,
        carId: d.carId,
        extProdParts: d.extProdParts,
        extProdPartner: d.extProdPartner,
        productParts: d.productParts,
        partsId: d.partsId,
        production: d.production,
        stock: d.stock,
      })),
      deletedItemIds: [...this.deletedItems],
      deletedDimensionIds: [...this.deletedDimensions],
    };
  }

  async save(): Promise<PreAccSaveResult> {
    const ref = this.currentRef;
    if (!ref) throw new Error(t("PreAccounting_NincsSzerkesztettSzamla"));
    this.isSaving = true;
    try {
      return await PreAccountingApi.save(ref.code, ref.invNo, this.buildSavePayload());
    } finally {
      this.isSaving = false;
    }
  }

  async reAccount(): Promise<PreAccReAccountResult> {
    const ref = this.currentRef;
    if (!ref) throw new Error(t("PreAccounting_NincsSzerkesztettSzamla"));
    this.isSaving = true;
    try {
      return await PreAccountingApi.reAccount(ref.code, ref.invNo, this.originalAccStatus);
    } finally {
      this.isSaving = false;
    }
  }

  // K2D w_note_cont: a megjegyzés önálló, azonnali mentés (nem a normál mentéssel megy).
  // Siker után a betöltött és az eredeti snapshot is frissül, hogy a sor ne tűnjön
  // módosítottnak a következő szerkesztésnél.
  async saveItemNote(item: PreAccEditItem, note: string) {
    const ref = this.currentRef;
    if (!ref) throw new Error(t("PreAccounting_NincsSzerkesztettSzamla"));
    const saved = await PreAccountingApi.saveItemNote(ref.code, ref.invNo, item.id, note);
    item.note = saved;
    const original = this.originalItems.find((x) => x.id === item.id);
    if (original) original.note = saved;
    return saved;
  }

  async isValidPartCode(partCode: string) {
    return await PreAccountingApi.validatePart(partCode);
  }

  async ValidPartnerName(partnerName: string) {
    return await PreAccountingApi.validatePartner(partnerName);
  }
}

const store = new PreAccountingStore();
export default store;
